import { CopilotResponse, ReferenceType } from '@careerhub/api';
import { Citation } from '@fluentai/react-copilot';
import React from 'react';

/** The chat-relevant parts of a copilot's response. */
type ParsedResponse = {
  /**
   * Natural language with citations interspersed.
   * String fragments at even indices, citations at odd indices.
   * @example [<>{'Here is a thing '}</>, <Citation {...props}>1</Citation>, <>{'\n- And another thing '}</>, <Citation {...props}>2</Citation>]
   */
  replyElements: JSX.Element[];
  /** URLs, one for each unique citation */
  referenceURLs: { [key: string]: string };
};

export function parseResponse(
  response: Pick<CopilotResponse, 'reply' | 'followupQuestions'> & Partial<Pick<CopilotResponse, 'references'>>,
  onCitationClicked: (url: string) => void
): ParsedResponse {
  const trimmedReply = response.reply?.trim() ?? '';
  const replyParts = trimmedReply.split(/\[([^\]]+)\]/g);
  const refs = response.references?.filter((ref) => ref.type === ReferenceType.RoleLibrary);

  if (refs && refs.length > 0) {
    return mapReplyParts(replyParts, refs[0].roleLibraryReferences ?? {}, onCitationClicked);
  } else {
    return {
      replyElements: replyParts.map((part) => {
        return <>{part}</>;
      }),
      referenceURLs: {},
    };
  }
  //}
}

interface citationDictionary {
  [key: string]: number;
}

function getCitationValue(dictionary: citationDictionary, key: string) {
  if (key in dictionary) {
    return dictionary[key];
  } else {
    let maxIndex = 0;
    for (const key in dictionary) {
      if (dictionary[key] > maxIndex) {
        maxIndex = dictionary[key];
      }
    }
    dictionary[key] = maxIndex + 1;
    return maxIndex + 1;
  }
}

/** @example replyParts === ['Here is a thing ', '0', ' another thing ', '1', ' and the first thing again ', '0'] */
function mapReplyParts(
  replyParts: string[],
  referenceURLs: { [key: string]: string },
  onCitationClicked: (url: string) => void
) {
  const dictionary: citationDictionary = {};
  const replyElementsResult: (JSX.Element | undefined)[] = replyParts?.map((part, index) => {
    if (index % 2 === 0) {
      return <>{part}</>;
    } else {
      const citationNumber = getCitationValue(dictionary, part);
      if (isNaN(citationNumber)) {
        console.warn(`Could not parse citation text: ${part}`);
        return <></>;
      }
      if (citationNumber > 0) {
        return (
          <Citation
            onClick={(e) => {
              e.preventDefault();
              onCitationClicked(part);
            }}
            referenceHref={part}
            aria-label={`Citation ${citationNumber}`}
          >
            {citationNumber}
          </Citation>
        );
      } else return undefined;
    }
  });
  const replyElements: JSX.Element[] = replyElementsResult.filter((element) => element != undefined) as JSX.Element[];
  return { replyElements, referenceURLs };
}
